import { TelemetryRecorder, type MatchTelemetry } from '../analysis/telemetry';
import {
  makeReplay,
  runReplay,
  type Action,
  type MatchConfig,
  type MatchEvent,
  type MatchState,
  type Side,
} from '../engine';
import { readJson, removeKey, writeJson } from './storage';
import { APP_VERSION } from './version';

/** Local store for playtest telemetry (§21). Nothing leaves the browser unless exported. */

const KEY = 'telemetry.v1';
const ID_KEY = 'anon-id.v1';
const MAX_MATCHES = 200;

export const loadTelemetry = (): MatchTelemetry[] =>
  readJson(KEY, (v) => (Array.isArray(v) ? (v as MatchTelemetry[]) : null)) ?? [];

export function appendTelemetry(record: MatchTelemetry): void {
  writeJson(KEY, [...loadTelemetry(), record].slice(-MAX_MATCHES));
}

export const clearTelemetry = (): void => removeKey(KEY);

/** A random id for this browser, used as the anonymous player id (§21.1). */
export function anonymousId(): string {
  const existing = readJson(ID_KEY, (v) => (typeof v === 'string' ? v : null));
  if (existing) return existing;
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `anon-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
  writeJson(ID_KEY, id);
  return id;
}

/** A recorder attached to a match; catches up on actions already played when resuming. */
export function telemetryFor(
  config: MatchConfig,
  seed: string,
  ids: Readonly<Record<Side, string>>,
  previous: readonly Action[] = [],
): {
  record: (b: MatchState, a: Action, s: MatchState, e: readonly MatchEvent[]) => void;
  finish: (s: MatchState) => void;
} {
  const recorder = new TelemetryRecorder({
    anonymousIds: ids,
    bestScores: true,
    appVersion: APP_VERSION,
  });
  recorder.start(config, seed);
  if (previous.length > 0)
    runReplay(makeReplay(config, seed, previous), previous.length, (b, a, s, e) =>
      recorder.record(b, a, s, e),
    );
  return {
    record: (b, a, s, e) => recorder.record(b, a, s, e),
    finish: (s) => appendTelemetry(recorder.finish(s)),
  };
}
